import type { FullTestResults, SingleTestResult } from "@/types/ui"
import type { AtsCompositeDataRequest, AtsEntityMapping } from "@/types/ats-integration-api"
import type { MappingConfig, ValueMapping } from "@/types/fbo-api"
import { useApi, ApiError } from "@/lib/api"
import { API_ENDPOINTS } from "@/lib/api"
import { useAtsSystemCode } from "@/hooks/use-ats-system-code"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"

interface TestFullMappingsParams {
  primaryEntityId: string
  config: MappingConfig
}

interface TestSingleSourceFieldParams {
  entityType: string
  entityId: string
  sourceField: string
  atsSource: string
}

interface TestResult<T> {
  success: boolean
  data?: T
  validationMessage?: string
}

// Helper function to get nested value using dot notation
const getNestedValue = (obj: any, path: string): any => {
  const value = path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : null
  }, obj)
  
  // Serialize objects to JSON strings for display
  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    return JSON.stringify(value, null, 2)
  }
  
  return value
}

export function useTestMappings() {
  const { t } = useTranslation()
  const { atsIntegrationApi } = useApi()
  const { getAtsSystemCode } = useAtsSystemCode()

  // Helper function for common error handling
  const handleApiError = <T>(err: unknown, operation: string): TestResult<T> => {
    console.error(`Failed to ${operation}:`, err)
    
    // Check if it's a 400 status code (validation error) or 404 (not found)
    if (err instanceof ApiError && (err.statusCode === 400 || err.statusCode === 404)) {
      // Extract error message from ProblemDetails response (RFC 7807)
      const validationMessage = err.errorData?.detail
      
      return {
        success: false,
        validationMessage
      }
    }
    
    // For other errors, throw them
    throw err
  }

  const testFullMappings = async ({ primaryEntityId, config }: TestFullMappingsParams): Promise<TestResult<FullTestResults>> => {
    // Validate input
    if (!primaryEntityId.trim() || primaryEntityId === "0") {
      return {
        success: false,
        validationMessage: t("testing.validation.primaryEntityIdRequired")
      }
    }

    try {
      // Add primary entity ID to the config entities
      const entities: AtsEntityMapping[] = config.entities.map((entity, index) => ({
        ...entity,
        id: index === 0 ? primaryEntityId : null, // Primary entity (first in array) gets the ID
        fieldMappings: entity.fieldMappings.map(mapping => ({
          ...mapping,
          valueMappings: mapping.valueMappings ?? null,
          isRequired: false,
          isValueMappingRequired: false
        }))
      }))

      const request: AtsCompositeDataRequest = {
        entities: entities
      }

      // Get the atsSystem code from atsSource
      const atsSystemCode = getAtsSystemCode(config.atsSource)

      // Call the ATS Integration API
      const response = await atsIntegrationApi.post<any>(
        API_ENDPOINTS.ATS_INTEGRATION.MAP(atsSystemCode), // Use atsSystem code
        request
      )

      // Convert response to FullTestResults format
      const allMappings: Array<{
        destinationField: string
        sourceField: string
        entityName: string
        valueMappings?: ValueMapping[]
      }> = []
      
      config.entities.forEach(entity => {
        entity.fieldMappings.forEach(mapping => {
          allMappings.push({
            destinationField: mapping.destinationField,
            sourceField: mapping.sourceField,
            entityName: entity.name,
            valueMappings: mapping.valueMappings
          })
        })
      })

      const results: FullTestResults = {
        entityId: primaryEntityId,
        mappings: allMappings.map(mapping => ({
          destinationField: mapping.destinationField,
          sourceField: mapping.sourceField,
          destinationValue: getNestedValue(response, mapping.destinationField),
          entity: mapping.entityName,
        })),
      }

      return {
        success: true,
        data: results
      }
    } catch (err) {
      return handleApiError<FullTestResults>(err, 'test full mappings')
    }
  }

  const testSingleSourceField = async ({ 
    entityType, 
    entityId, 
    sourceField, 
    atsSource 
  }: TestSingleSourceFieldParams): Promise<TestResult<SingleTestResult>> => {
    // Validate input
    if (!entityType.trim()) {
      return {
        success: false,
        validationMessage: t("testing.validation.entityTypeRequired")
      }
    }

    if (!entityId.trim() || entityId === "0") {
      return {
        success: false,
        validationMessage: t("testing.validation.entityIdRequired")
      }
    }

    if (!sourceField.trim()) {
      return {
        success: false,
        validationMessage: t("testing.validation.sourceFieldRequired")
      }
    }

    try {
      // Create a minimal request for testing a single field
      const request: AtsCompositeDataRequest = {
        entities: [{
          entityType: entityType,
          name: entityType,
          id: entityId,
          referenceId: null,
          fieldMappings: [{
            sourceField: sourceField,
            destinationField: "TestField", // Use a dummy destination field for testing
            isRequired: false,
            dataTypeConverter: null,
            valueMappings: null,
            isValueMappingRequired: false,
            defaultValueMapping: null
          }]
        }]
      }

      // Get the atsSystem code from atsSource
      const atsSystemCode = getAtsSystemCode(atsSource)

      // Call the ATS Integration API
      const response = await atsIntegrationApi.post<any>(
        API_ENDPOINTS.ATS_INTEGRATION.MAP(atsSystemCode), // Use atsSystem code
        request
      )

      // Check if the field was found and has a value
      const sourceValue = getNestedValue(response, "TestField")
      const success = sourceValue !== undefined // null is a valid value
      const error = success ? null : t("testing.validation.fieldNotFound")

      const result: SingleTestResult = {
        entityType,
        entityId,
        sourceField,
        sourceValue: sourceValue ?? null,
        success,
        error,
      }

      return {
        success: true,
        data: result
      }
    } catch (err) {
      return handleApiError<SingleTestResult>(err, 'test single source field')
    }
  }

  return {
    testFullMappings,
    testSingleSourceField,
  }
} 